import React, { FC, useState } from 'react';
import styled, { css } from 'styled-components';
import media from '../../../constants/media';
import theme from '../../../constants/theme';
import { useClientOrServerSideRouting } from '../../../helpers/routing/routeHelpers';
import { IImageCard } from '../../../interfaces/umbracoNodes/contentSlices';
import TextLinkWithArrow from '../../textLinkWithArrow';

const CardContainer = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	width: 100%;
	padding: 23px;
	background: ${theme.colors.white};
	border-radius: 16px;

	${media.desktopSmall`
        padding: 32px;
    `};
`;

export const overlayStyle = css`
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	border-radius: 16px;
	box-shadow: 0 0 30px rgba(0, 0, 0, 0.08);
	transition: box-shadow 0.3s ease-in-out;

	${media.desktopSmall`
		&:hover {
			box-shadow: 0 0 50px rgba(0, 0, 0, 0.15);
		}
	`}
`;

export const SimpleOverlay = styled.div`
	${overlayStyle}
`;

const ClickOverlay = styled.div`
	${overlayStyle}
	cursor: pointer;
`;

const ContentWrapper = styled.div`
	${media.desktopSmall`
		display: flex;
		align-items: center;
	`}
`;

const Image = styled.img`
	width: 128px;
	height: 128px;
	flex-shrink: 0;
	border-radius: 8px;
	object-fit: cover;
`;

const TextWrapper = styled.div`
	margin-top: 32px;

	${media.desktopSmall`
        margin-top: 0;
        margin-left: 32px;
		width: calc(100% - 160px);
	`}
`;

const Title = styled.h4`
	font-size: 32px;
	letter-spacing: -0.04em;
	line-height: 1.2;
	color: ${theme.colorsV2.mainRed};
	font-family: ${theme.fonts.SharpSlab};
	font-weight: ${theme.fonts.weight.semiBold};
	overflow-wrap: break-word;
`;

const Description = styled.p`
	margin: 8px 0 0;
	padding: 0;
	font-family: ${theme.fonts.Arimo};
	color: ${theme.colorsV2.mainDark};
	font-size: 18px;
	line-height: 1.4;
`;

const TextLinkWithArrowStyled = styled(TextLinkWithArrow)`
	margin: 8px 0 0 auto;
`;

interface Props {
	content: IImageCard;
	className?: string;
}

const ImageCard: FC<Props> = ({ content, className }) => {
	const [mouseMoved, setMouseMoved] = useState(false);
	const routeToClientOrServerRoute = useClientOrServerSideRouting();

	const handleClick = (to: string) => {
		if (!mouseMoved) {
			routeToClientOrServerRoute(to);
		}
	};

	return (
		<CardContainer className={className}>
			{content.Values.Link && (
				<ClickOverlay
					role="link"
					data-href={content.Values.Link.Url}
					tabIndex={0}
					onMouseMove={() => setMouseMoved(true)}
					onMouseDown={() => setMouseMoved(false)}
					onMouseUp={() => handleClick(content.Values.Link.Url)}
				/>
			)}
			{!content.Values.Link && <SimpleOverlay />}
			<ContentWrapper>
				<Image src={content.Values.Image.Src} sizes="128px, 128px" />
				<TextWrapper>
					<Title>{content.Values.Title}</Title>
					<Description>{content.Values.Description}</Description>
				</TextWrapper>
			</ContentWrapper>
			{content.Values.Link && content.Values.DisplayLink && (
				<TextLinkWithArrowStyled
					href={content.Values.Link.Url}
					title={content.Values.Link.Name}
					tabIndex={-1}
				/>
			)}
		</CardContainer>
	);
};

export default ImageCard;
